import React from 'react';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ModalOverlay,
  ModalContainer,
  TopSection,
  CongratsText,
  PackageTitle,
  PackageTitleBig,
  UnderlineSvg,
  BottomSection,
  RewardText,
  HighlightNumber,
  ActionButton,
  ButtonIcon,
  TextContent,
  CloseButtonWrapper,
  ModalWrapper,
  AnimationContainer,
} from './styles';
import CloseButton from '../../../common/CloseButton';
import { BenefitItem, BenefitType } from '@/services/types';
import { handleGoToCreation } from '@/hooks/useUserBenefits';

// 奖励类型枚举
export enum RewardType {
  MONTHLY_LOGIN = 'monthly_login', // 每月登录奖励
  PAYMENT_SUCCESS = 'payment_success', // 支付成功奖励
}

interface MonthlyRewardModalProps {
  onClose: () => void;
  benefitsData?: BenefitItem[];
  rewardType?: RewardType; // 新增：奖励类型，默认为每月登录奖励
}

const MonthlyRewardModal: React.FC<MonthlyRewardModalProps> = ({
  onClose,
  benefitsData,
  rewardType = RewardType.MONTHLY_LOGIN, // 默认为每月登录奖励
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  // 根据奖励类型计算积分和显示内容
  const rewardInfo = React.useMemo(() => {
    if (!benefitsData) {
      return {
        credits: 88,
        title: t('rewardModal.monthlyLoginReward'),
        creditValue: 8.8,
      };
    }

    if (rewardType === RewardType.PAYMENT_SUCCESS) {
      // 支付成功奖励：计算所有类型的权益总和
      const totalCredits = benefitsData.reduce((total, benefit) => total + benefit.credits, 0);
      return {
        credits: totalCredits || 88,
        title: t('rewardModal.paymentPackageReward'),
        creditValue: (totalCredits || 88) / 10,
      };
    } else {
      // 每月登录奖励：只计算每月登录权益
      const monthlyBenefits = benefitsData.filter(
        benefit => benefit.type === BenefitType.MONTHLY_REWARD
      );
      const monthlyCredits = monthlyBenefits.reduce(
        (total, benefit) => total + benefit.credits,
        88
      );
      return {
        credits: monthlyCredits,
        title: t('rewardModal.monthlyLoginReward'),
        creditValue: monthlyCredits / 10,
      };
    }
  }, [benefitsData, rewardType, t]);

  // 注意：时间戳更新移到了 onClose 回调中，确保只有用户真正看到弹窗后才标记为已显示

  // 处理点击遮罩层关闭
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalWrapper>
        <CloseButtonWrapper>
          <CloseButton onClick={onClose} />
        </CloseButtonWrapper>
        <ModalContainer>
          <TopSection>
            <TextContent>
              <CongratsText>{t('rewardModal.congrats')}</CongratsText>
              {rewardType === RewardType.PAYMENT_SUCCESS && <PackageTitle>{rewardInfo.title}</PackageTitle>}
              {rewardType !== RewardType.PAYMENT_SUCCESS && <PackageTitleBig>{rewardInfo.title}</PackageTitleBig>}
              <UnderlineSvg
                xmlns="http://www.w3.org/2000/svg"
                width="214"
                height="11"
                viewBox="0 0 214 11"
                fill="none"
              >
                <path
                  d="M1.0033 6.39478C37.0389 2.37523 237.309 -4.2158 210.037 9.7401"
                  stroke="#FF6D49"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </UnderlineSvg>
            </TextContent>
            <AnimationContainer />
          </TopSection>
          <BottomSection>
            <RewardText>
              {/* <HighlightNumber>{rewardInfo.creditValue}</HighlightNumber> 米粒值，已发放到您账户中 */}
              {t('rewardModal.accountStatement')}
            </RewardText>
            <ActionButton onClick={() => handleGoToCreation(location.pathname, onClose)}>
              {t('rewardModal.goCreate')}
              <ButtonIcon
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
              >
                <g clipPath="url(#clip0_2618_432)">
                  <path
                    d="M13.9304 6.8957C13.9024 6.7137 13.8114 6.5387 13.6714 6.4197L8.33744 1.0857C8.01544 0.812697 7.53244 0.847697 7.25244 1.1697C7.00744 1.4567 7.00744 1.8837 7.25244 2.1707L11.3194 6.2377H0.875436C0.448436 6.2377 0.105436 6.5807 0.105436 7.0077C0.105436 7.4347 0.448436 7.7777 0.875436 7.7777H11.3194L7.25244 11.8447C6.95144 12.1457 6.95144 12.6287 7.24544 12.9297C7.54644 13.2307 8.02944 13.2307 8.33044 12.9367L13.7204 7.5537C13.8884 7.3787 13.9654 7.1337 13.9304 6.8957Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_2618_432">
                    <rect width="14" height="14" fill="white" />
                  </clipPath>
                </defs>
              </ButtonIcon>
            </ActionButton>
          </BottomSection>
        </ModalContainer>
      </ModalWrapper>
    </ModalOverlay>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default MonthlyRewardModal;
